import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PAGE_BACK_DESKTOP_ONLY_CLASS } from '@/member/page-back-link';

import { usePublicOrganizerProfile } from './api';
import { PROFILE_COPY } from './constants';
import {
  NoOrganizerHere,
  OrganizerProfilePanel,
  OrganizerProfileSkeleton,
} from './organizer-profile-panel';

/**
 * The same organizer profile, **inside the member app** (`MEM-018`).
 *
 * ## The defect this route closes
 *
 * The member-app parity pass, §10: the organizer profile *"renders inside the **marketing**
 * chrome — top bar with 'H2HSpark / For organizers / Open the app', and a footer — so a member
 * who taps 'Hosted by Riverside Founders Network' on an event **loses the tab bar and the app
 * shell**. The prototype treats it as a member-app screen with a back arrow returning to the
 * event."*
 *
 * This is that screen. Mounted under `/app` inside `MemberLayout`, so the shell and the tab bar
 * stay exactly where they were, and carrying the prototype's back circle over the gradient band.
 *
 * ## Public, like the rest of `/app`'s discovery surfaces
 *
 * **Not** wrapped in `ProtectedRoute`. `MEM-019` AC-1 keeps Discover and every published event's
 * detail page available with no account, and an organizer profile is the same kind of thing — a
 * member arriving from a shared event link is often signed out. `App.tsx` gates the routes that
 * need a session individually rather than gating the shell, which is what makes this possible.
 *
 * The consequence to be deliberate about: a signed-out visitor **can** reach app chrome here, by
 * typing this path. That is fine and is already true of `/app` itself — the shell renders for
 * everybody by design, so that reaching for a gated tab produces the sign-in interstitial rather
 * than the app vanishing. What must not happen is the reverse, and does not: nothing sends a
 * signed-out visitor at `h2hspark.com/{slug}` here.
 *
 * ## Back goes back, and falls back to Discover
 *
 * `navigate(-1)` when there is somewhere to return to, which is the prototype's "returning to the
 * event" for the journey that matters — a member came from an event page. A member who opened
 * this path directly has no history to pop, and a back arrow that does nothing is worse than one
 * that goes somewhere sensible, so the fallback is Discover.
 */
export function MemberOrganizerProfilePage(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const query = usePublicOrganizerProfile(slug ?? '');

  const back = (
    <button
      type="button"
      aria-label="Back"
      onClick={() => {
        if (window.history.length > 1) navigate(-1);
        else void navigate('/app');
      }}
      /* Desktop only, like every other page back control since Sumit's ruling of
         2026-09-11: the phone bar carries it, and a second one six pixels below the first is the
         same action twice. `page-back-link.tsx` says why the class is written there and named
         here rather than repeated. What changed with the 10 September header (§2.8) is the
         **treatment**: the band it used to float over is gone, so this is the prototype's inline
         text link above the header rather than a round button over a gradient. */
      className={`${PAGE_BACK_DESKTOP_ONLY_CLASS} mb-5 items-center gap-1.5 font-brand text-sm font-bold text-muted-foreground transition-colors hover:text-primary`}
    >
      <ArrowLeft className="h-[17px] w-[17px]" aria-hidden />
      {BACK_LABEL}
    </button>
  );

  if (query.isPending) return <OrganizerProfileSkeleton />;
  if (query.isError || query.data === undefined) return <NoOrganizerHere />;

  return <OrganizerProfilePanel profile={query.data} backSlot={back} />;
}

/**
 * The back control's words — `Web App.dc.html` §2.8's *"Back to events"*.
 *
 * The screen **used to bleed out through the shell's padding**, because the header was a
 * full-bleed gradient band and a band inset by 16px with the shell behind it is a different
 * design. The 10 September header sits on the page's own ground (§2.8), so the shell's `px-4 pt-6`
 * is right for this screen as it is for every other, and the back control is the prototype's text
 * link above the header rather than a circle floating over a gradient.
 */
const BACK_LABEL = PROFILE_COPY.backToEvents;
